import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { captureException } from '@sentry/react';
import { LoadingSpinner } from 'components/ui';
import querystring from 'querystring';
import { ReactNode } from 'react';
import { toast } from 'react-toastify';
import { useAppSelector } from 'store';
import {
  useGetUserInfoQuery,
  useSelectSubscriptionPlanMutation,
} from 'store/services/segmentsApi';
import { SubscriptionPlan } from 'store/services/types';
import { userDetailsState } from 'store/userDetailsSlice';
import { useQuery } from 'utils/useQuery';

export interface Plan {
  id: number;
  label: string;
  name: SubscriptionPlan;
  price: number;
  description: string[];
  features: string[];
  featured: boolean;
}

export type Plans = Plan[];

export const PriceTable = ({ plans }: { plans: Plans }) => {
  const paidPlans = plans
    .sort((a, b) => a.id - b.id)
    .filter((plan) => !!!plan.name.toLowerCase().includes('free'));

  const freePlan = plans.find((plan) =>
    plan.name.toLocaleLowerCase().includes('free')
  )!;

  return (
    <div>
      <div className="lg:mt-6 lg:grid lg:grid-cols-3">
        {paidPlans.map((plan) => (
          <SubscriptionPlanCard plan={plan} key={plan.id} />
        ))}
      </div>
      <div className="mt-6 text-segments-13px p-3 bg-[#FFECD6] rounded-xl inline-block">
        <div className="space-y-3 ">
          <div className="flex space-x-3">
            <ExclamationCircleIcon className="w-5 h-5" />
            <p className="text-black text-opacity-60">
              Feature availability during the trial will be based on the plan
              selected.
            </p>
          </div>
        </div>
      </div>
      <hr className="mt-6" />
      {freePlan && <FreePlanFooter plan={freePlan} />}
    </div>
  );
};

function SubscriptionPlanCard({ plan }: { plan: Plan }) {
  const query = useQuery();

  const storeName = query.get('store_name');

  const { currentStore } = useAppSelector(userDetailsState);
  const { data: userInfo } = useGetUserInfoQuery();

  const hasStoreSelectedPlan = userInfo?.onboard_objectives.find(
    (store) => store.store_name === storeName
  )?.plan_selected;

  const currentSubscriptionPlan = currentStore.completeInfo?.active_sub?.level;

  const [selectPlan, { isLoading, originalArgs, data }] =
    useSelectSubscriptionPlanMutation();

  const planSelectedResponseStatus = Boolean(data?.status);

  const handleSelectPlan = (planName: SubscriptionPlan) => {
    if (!!!storeName || isLoading) return;
    selectPlan({
      serializedStoreName: querystring.stringify({ store_name: storeName }),
      plan: planName,
    })
      .unwrap()
      .then((response) => {
        toast.success(
          `You’ve selected the ${plan.label} plan. You will be redirected to Shopify to accept the plan`
        );

        window.location.href = response.confirmation_url;
      })
      .catch((error) => {
        captureException(error);
      });
  };

  return (
    <div>
      <div
        className={`rounded-t-[30px] font-bold text-segments-13px uppercase text-white bg-segments-accent-color-1 px-9 py-[10px]  ${
          plan.featured ? 'visible' : 'invisible'
        }`}
      >
        <p>Most Popular</p>
      </div>
      <div
        className={`pt-7 pb-16 ${
          plan.featured &&
          'bg-segments-accent-green-3 bg-opacity-30 rounded-b-[30px]'
        } `}
      >
        <div className=" px-[35px]">
          <p
            className={`text-xl font-semibold ${
              plan.featured ? 'text-segments-accent-color-1' : 'black'
            }`}
          >
            {plan.label}
          </p>
          <div className="mt-2">
            {plan.description.map((item, index) => (
              <p className="text-black text-opacity-50" key={index}>
                {item}
              </p>
            ))}
          </div>
          <div className="flex items-center mt-10 space-x-2">
            <p className="text-[44px]">
              {plan.price.toLocaleString(undefined, {
                style: 'currency',
                currency: 'usd',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              })}
            </p>
            <p className="text-black text-segments-13px text-opacity-30">
              USD/mo
            </p>
          </div>
          <div className="mt-2">
            <SelectPlanButton
              onClick={() => handleSelectPlan(plan.name)}
              disabled={!!!storeName || isLoading || planSelectedResponseStatus}
              isLoading={isLoading && originalArgs?.plan === plan.name}
              currentSelectedPlan={Boolean(
                hasStoreSelectedPlan &&
                  currentSubscriptionPlan?.toLowerCase() ===
                    plan.label.toLowerCase()
              )}
            >
              {hasStoreSelectedPlan ? <p>Select plan</p> : <p>Try for free</p>}
            </SelectPlanButton>
            <p className="mt-2 text-center text-black text-opacity-30 text-segments-13px">
              Charged via Shopify billing
            </p>
          </div>
          <div className="mt-[22px] space-y-2">
            {plan.features.map((item, index) => (
              <div className="flex items-center space-x-2" key={index}>
                <CheckIcon />
                <p className="tracking-wide text-segments-13px">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function SelectPlanButton({
  onClick,
  disabled,
  isLoading,
  currentSelectedPlan,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  isLoading: boolean;
  currentSelectedPlan: boolean;
  children: ReactNode;
}) {
  return (
    <button
      className={`w-full py-[14px] bg-segments-accent-color-1 rounded-2xl font-bold text-white ${
        currentSelectedPlan
          ? 'bg-segments-accent-color-orange opacity-60 pointer-events-none text-white'
          : ''
      }  disabled:opacity-30 disabled:cursor-not-allowed`}
      onClick={onClick}
      disabled={disabled}
    >
      {isLoading ? (
        <LoadingSpinner className="w-6 h-6 mx-auto" />
      ) : (
        <>{currentSelectedPlan ? <p>Current plan</p> : children}</>
      )}
    </button>
  );
}
function FreePlanFooter({ plan }: { plan: Plan }) {
  const query = useQuery();

  const storeName = query.get('store_name');

  const [selectPlan, { isLoading }] = useSelectSubscriptionPlanMutation();

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (!!!storeName || isLoading) return;
    selectPlan({
      serializedStoreName: querystring.stringify({ store_name: storeName }),
      plan: plan,
    })
      .unwrap()
      .then((response) => {
        toast.success("You've successfully subscribed to the selected plan");

        window.location.href = response.confirmation_url;
      });
  };
  return (
    <div className="my-6 space-y-2">
      <p>
        Cancel my subscription?{' '}
        <button
          onClick={() => handleSelectPlan(plan.name)}
          className="font-bold text-segments-link"
        >
          Select our free plan
        </button>
      </p>
      <p className="text-black text-opacity-50 text-segments-13px">
        Free plan offers{' '}
        {plan.features.map((feature, index) => (
          <span className="lowercase" key={index}>
            {feature}
            {index !== plan.features.length - 1 && ', '}
          </span>
        ))}
      </p>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="21" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M18 10c0-4.14-3.36-7.5-7.5-7.5C6.36 2.5 3 5.86 3 10c0 4.14 3.36 7.5 7.5 7.5 4.14 0 7.5-3.36 7.5-7.5Z"
        stroke="#54B363"
        strokeMiterlimit="10"
      />
      <path
        d="M14.25 6.875 9 13.125l-2.25-2.5"
        stroke="#000"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
